import { log, logMethodCall } from './debug';
import {
  INT_RADIX,
} from '../const/common';
import Event from '../const/event';

/**
 * This fuction will match passed attribute id from channel attributes
 *
 * @param {String} attributeId Attribute id
 * @param {String} attributeValue Attribute value
 * @param {() => void} callback callback function (optional)
 */
export function hasAttribute(context, attributeId, attributeValue, callback) {
  logMethodCall('hasAttribute', attributeId);

  context.waitForInit().then(() => {
    if (attributeId) {
      context.subscriptionManager.storageManager.hasAttribute(attributeId, attributeValue).then((hasAnyAttribute) => {
        if (typeof callback === 'function') {
          callback(hasAnyAttribute);
        }
      });
    } else {
      log.error('attribute id not specified');
    }
  });
}

export function setAttribute(context, attributeId, valueParam, callback) {
  logMethodCall('setAttribute', attributeId, valueParam);

  let value = valueParam;

  if (typeof value === 'object') {
    value = JSON.stringify(value);
  }

  const sendToApi = ({ subscriptionId, alradyExists }) => {
    if (!subscriptionId || alradyExists) {
      return;
    }

    context.api.setSubscriptionAttribute(subscriptionId, attributeId, valueParam).then(() => {
      if (typeof callback === 'function') {
        callback(false, true);
      }
    });
  };

  const saveToStorage = () => {
    if (localStorage.getItem(`cleverpush-attribute-${attributeId}`) === (`${value}`)) {
      return { alreadyExists: true };
    }
    localStorage.setItem(`cleverpush-attribute-${attributeId}`, value);
    return { alreadyExists: false };
  };

  context.waitForConfigAvailable().then(() => {
    if (context.config.attributesWithoutSubscriptionEnabled) {
      const { alreadyExists } = saveToStorage();
      context.waitForInit().then(() => {
        context.waitForSubscription().then((subscriptionId) => {
          sendToApi({ subscriptionId, alreadyExists });
        });
      });
    } else {
      context.waitForInit().then(() => {
        context.waitForSubscription().then((subscriptionId) => {
          const { alreadyExists } = saveToStorage();
          sendToApi({ subscriptionId, alreadyExists });
        });
      });
    }
  });
}

export function incAttribute(context, attributeId, incParam, callback) {
  logMethodCall('incAttribute', attributeId, incParam);

  let inc = parseInt(incParam, INT_RADIX);
  if (isNaN(inc)) {
    inc = 1;
  }

  let value = parseInt(localStorage.getItem(`cleverpush-attribute-${attributeId}`), INT_RADIX) || 0;
  value += inc;

  context.waitForInit().then(() => {
    context.waitForSubscription().then((subscriptionId) => {
      localStorage.setItem(`cleverpush-attribute-${attributeId}`, `${value}`);

      context.api.setSubscriptionAttribute(subscriptionId, attributeId, value).then(() => {
        if (typeof callback === 'function') {
          callback(false, true);
        }
      });
    });
  });
}

export function getAttribute(context, attributeId, callback) {
  logMethodCall('getAttribute', attributeId);

  context.waitForInit().then(() => {
    context.waitForSubscription().then(() => {
      callback(localStorage.getItem(`cleverpush-attribute-${attributeId}`));
    });
  });
}

export function pushAttributeValue(context, attributeId, valueParam, optionsParam, callbackParam) {
  logMethodCall('pushAttributeValue', attributeId, valueParam);

  let value = valueParam;
  if (typeof value === 'object') {
    value = JSON.stringify(value);
  }

  let callback = callbackParam;
  let options = {};
  if (typeof optionsParam === 'function') {
    callback = optionsParam;
  } else if (typeof optionsParam === 'object') {
    options = optionsParam;
  }

  context.waitForInit().then(() => {
    context.waitForSubscription().then((subscriptionId) => {
      let alreadySet = false;

      const storageStr = localStorage.getItem(`cleverpush-attribute-${attributeId}`);
      if (storageStr) {
        try {
          const array = JSON.parse(storageStr);
          if (array && array.length) {
            if (array.indexOf(value) > -1) {
              alreadySet = true;
            } else {
              localStorage.setItem(`cleverpush-attribute-${attributeId}`, JSON.stringify([...array, value]));
            }
          } else {
            localStorage.setItem(`cleverpush-attribute-${attributeId}`, JSON.stringify([value]));
          }
        } catch (ignored) {
          localStorage.setItem(`cleverpush-attribute-${attributeId}`, JSON.stringify([value]));
        }
      } else {
        localStorage.setItem(`cleverpush-attribute-${attributeId}`, JSON.stringify([value]));
      }

      if (alreadySet && !options.force) {
        return;
      }

      context.api.pushSubscriptionAttributeValue(subscriptionId, attributeId, valueParam).then(() => {
        if (typeof callback === 'function') {
          callback(false, true);
        }
      });
    });
  });
}

export function pullAttributeValue(context, attributeId, valueParam, callback) {
  logMethodCall('pullAttributeValue', attributeId, valueParam);

  let value = valueParam;
  if (typeof value === 'object') {
    value = JSON.stringify(value);
  }

  this.waitForInit().then(() => {
    this.waitForSubscription().then((subscriptionId) => {
      const storageStr = localStorage.getItem(`cleverpush-attribute-${attributeId}`);
      if (storageStr) {
        try {
          const array = JSON.parse(storageStr);
          if (array && array.length) {
            if (array.indexOf(value) > -1) {
              array.splice(array.indexOf(value), 1);
              localStorage.setItem(`cleverpush-attribute-${attributeId}`, JSON.stringify(array));
            }
          }
        } catch (error) {
          log.info('Error pulling attribute value', error);
        }
      }

      this.api.pullSubscriptionAttributeValue(subscriptionId, attributeId, valueParam).then(() => {
        if (typeof callback === 'function') {
          callback(false, true);
        }
      });
    });
  });
}

export function initAttributes(context) {
  (context.config.customAttributes || []).forEach((attribute) => {
    if (!attribute.javascriptFunctionEnabled || !attribute.javascriptFunction) {
      return;
    }

    try {
      const value = eval(attribute.javascriptFunction);
      if (typeof value === 'object' && value.then) {
        try {
          value.then((result) => {
            context.setAttribute(attribute.id, result);
          });
        } catch (err) {
          log.debug(err);
        }
      } else if (typeof value !== 'undefined') {
        context.setAttribute(attribute.id, value);
      }
    } catch (ignored) {
      log.debug(ignored);
    }
  });

  if (context.config.confirmAlertSelectAttributesLater) {
    return;
  }

  import('../selectAttributes').then(({ default: SelectAttributes }) => {
    context.selectAttributes = new SelectAttributes(context.config, context);
    context.subscriptionManager.isSubscribed().then((isSubscribed) => {
      if (isSubscribed) {
        context.selectAttributes.init();
      } else {
        context.on(Event.SUBSCRIBED, () => {
          context.selectAttributes.init();
        });
      }
    });
  });
}
